'use client';

import React, { useState } from 'react';

const registrationTypes = [
    { value: 'student', label: 'Student' },
    { value: 'parent', label: 'Parent' },
    { value: 'institution', label: 'Institution' },
    { value: 'tutor', label: 'Tutor' },
];

export default function EarlyAccessForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        userType: '',
    });

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);

        try {
            const response = await fetch(
                `${process.env.NEXT_PUBLIC_API_URL}/api/early-access`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message || 'Unable to submit your request.'
                );
            }

            setSubmitted(true);

        } catch (error) {
            console.error(
                'Early access submission error:',
                error
            );

            alert(
                error instanceof Error
                    ? error.message
                    : 'Something went wrong. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSubmitted(false);
        setFormData({
            name: '',
            email: '',
            userType: '',
        });
    };

    if (submitted) {
        return (
            <div className="bg-white border border-gray-200 rounded-2xl p-10 sm:p-12 flex flex-col items-center justify-center text-center min-h-[380px] shadow-sm">
                {/* Success Icon */}
                <div className="w-16 h-16 rounded-full bg-[#3757FF]/10 flex items-center justify-center mb-6">
                    <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                    >
                        <path
                            d="M6 14l5.5 5.5L22 8"
                            stroke="#3757FF"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                    You're on the list
                </h3>

                <p className="text-gray-500 leading-relaxed max-w-md">
                    Thanks for requesting early access to Wisly. We'll send you an
                    email with further information when testing begins.
                </p>

                <button
                    onClick={resetForm}
                    className="mt-8 px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    Submit another request
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-7 sm:p-9 shadow-sm">

            {/* Header */}
            <div className="mb-7">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">
                    Request early access
                </h2>

                <p className="text-sm sm:text-base text-gray-500 leading-relaxed">
                    Be among the first to experience Wisly when testing begins.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

                {/* Full Name */}
                <div>
                    <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                        Full name
                    </label>

                    <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full h-12 bg-white border border-gray-200 rounded-xl px-4 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-[#3757FF] focus:ring-2 focus:ring-[#3757FF]/10 transition-all"
                    />
                </div>

                {/* Email */}
                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                        Email address
                    </label>

                    <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className="w-full h-12 bg-white border border-gray-200 rounded-xl px-4 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-[#3757FF] focus:ring-2 focus:ring-[#3757FF]/10 transition-all"
                    />
                </div>

                {/* Registration Type */}
                <div>
                    <label
                        htmlFor="userType"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                        I want to register as
                    </label>

                    <select
                        id="userType"
                        name="userType"
                        required
                        value={formData.userType}
                        onChange={handleChange}
                        className="w-full h-12 bg-white border border-gray-200 rounded-xl px-4 text-gray-900 text-sm focus:outline-none focus:border-[#3757FF] focus:ring-2 focus:ring-[#3757FF]/10 transition-all appearance-none cursor-pointer"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7280' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 16px center',
                        }}
                    >
                        <option value="" disabled>
                            Select an option...
                        </option>

                        {registrationTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Submit */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#3757FF] hover:bg-[#2948E8] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                >
                    {loading ? (
                        <>
                            <svg
                                className="animate-spin"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <circle
                                    cx="8"
                                    cy="8"
                                    r="6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeDasharray="20"
                                    strokeDashoffset="10"
                                />
                            </svg>

                            Sending request...
                        </>
                    ) : (
                        <>
                            Request early access

                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M3 8h10M9 4l4 4-4 4"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </>
                    )}
                </button>

                {/* Small Disclaimer */}
                <p className="text-xs text-gray-400 text-center leading-relaxed pt-1">
                    We'll only use your email to provide information about Wisly's
                    early access testing.
                </p>

            </form>
        </div>
    );
}